import { socialCenter } from '@/sim/social/socialGrid';
import { hostShares, strainOf, STRAIN_CELL, GRAIN, newStrainMix, mixAt, hostOfKey,
  type StrainHost, type StrainTraits } from './strain';
import { effectiveTraitsAt, newTiltMix, StrainField } from './strainField';
import type { Region } from './region';
import type { BiomeKey } from './types';

/**
 * THE ECOLOGY'S ONE WINDOW ONTO THE STRAINS — cached where the world is
 * static, live where it is not.
 *
 * Every sim reader (the habitat's forage and fuel, the fire's spread, the
 * herds' capacity and mortality) asks this class and never the registry
 * directly, for one load-bearing reason: the BASELINE mixture is a pure
 * function of position and seed, so its trait blend per 260 m cell is a
 * constant of the world — computed once, cached forever, and the whole
 * island's baseline costs a few hundred milliseconds ONCE rather than per
 * world-day. Only the FIELD moves, and the field is sparse: a world with no
 * tilt anywhere (all of prehistory until the first fire regime, all of play
 * until the first press) pays one Map hit per read and not one mixture
 * evaluation past the warm-up. Law 2, applied to cost.
 *
 * The overlay path is exact, not approximate: where the field holds a tilt
 * whose host matters to the asking reader, the cached baseline is discarded
 * for that read and the full `effectiveTraitsAt` composition runs — the same
 * composition the renderer's phenotype source and the card use, so the
 * ecology can never disagree with the eye about what a valley has become.
 *
 * Determinism: every cache entry is a pure function of (region, host, cell),
 * and the tilt overlay is a pure function of (field state, now) — a restored
 * save recomputes byte-identical numbers, which `test/strain.ts` holds the
 * field to and the fingerprint suites hold the whole sim to.
 */
export class StrainReads {
  /** Baseline mixture-weighted traits per `${host}:${gx},${gz}` — static. */
  private readonly base = new Map<string, Float32Array>();
  /** Baseline biome-blend per `${gx},${gz}` (plant hosts × hostShares). */
  private readonly plantBase = new Map<string, Float32Array>();
  private readonly mix = newStrainMix(GRAIN.mixMax + 1);
  private readonly tilts = newTiltMix();
  private readonly scratch: StrainTraits = { yield: 1, tolerance: 1, exposure: 1, spread: 1 };

  constructor(
    private readonly region: Region,
    private readonly field: StrainField,
  ) {}

  /** The field's cache tag, for readers that key their own caches. */
  get version(): number { return this.field.version; }

  /** Whether any tilt of a PLANT host stands at this point right now. */
  private plantTiltAt(wx: number, wz: number, now: number): boolean {
    if (this.field.size === 0) return false;
    this.field.blendAt(wx, wz, now, this.tilts);
    for (let i = 0; i < this.tilts.n; i++) {
      const h = hostOfKey(this.tilts.key[i]);
      if (h && (h === 'broadleaf' || h === 'conifer' || h === 'shrub'
        || h === 'tuft' || h === 'spreading')) return true;
    }
    return false;
  }

  /** Whether a tilt of THIS host stands at this point right now. */
  private hostTiltAt(host: StrainHost, wx: number, wz: number, now: number): boolean {
    if (this.field.size === 0) return false;
    this.field.blendAt(wx, wz, now, this.tilts);
    for (let i = 0; i < this.tilts.n; i++) {
      if (hostOfKey(this.tilts.key[i]) === host) return true;
    }
    return false;
  }

  private baselineOf(host: StrainHost, gx: number, gz: number): Float32Array {
    const key = `${host}:${gx},${gz}`;
    const hit = this.base.get(key);
    if (hit) return hit;
    const cx = (gx + 0.5) * STRAIN_CELL, cz = (gz + 0.5) * STRAIN_CELL;
    mixAt(this.region, host, cx, cz, this.mix);
    let y = 0, t = 0, e = 0, s = 0;
    for (let i = 0; i < this.mix.n; i++) {
      const share = this.mix.share[i];
      const tr = strainOf(this.region.seed, this.mix.key[i]).traits;
      y += share * tr.yield; t += share * tr.tolerance;
      e += share * tr.exposure; s += share * tr.spread;
    }
    const out = new Float32Array([y, t, e, s]);
    this.base.set(key, out);
    return out;
  }

  /**
   * The effective traits of ONE host's ground at a point — the number the
   * wildlife multiplies. Baseline from the cache at cell grain; the exact
   * composition wherever the field holds a tilt of this host.
   */
  hostTraits(host: StrainHost, wx: number, wz: number, now: number, out: StrainTraits): void {
    if (this.hostTiltAt(host, wx, wz, now)) {
      effectiveTraitsAt(this.region, this.field, host, wx, wz, now, out, this.mix, this.tilts);
      return;
    }
    const b = this.baselineOf(host, Math.floor(wx / STRAIN_CELL), Math.floor(wz / STRAIN_CELL));
    out.yield = b[0]; out.tolerance = b[1]; out.exposure = b[2]; out.spread = b[3];
  }

  /** `hostTraits` at a social cell's own centre — the herd ledger's grain. */
  hostTraitsAtCell(host: StrainHost, sx: number, sz: number, now: number, out: StrainTraits): void {
    const c = socialCenter(sx, sz);
    this.hostTraits(host, c.x, c.z, now, out);
  }

  /**
   * The PLANT traits a square of ground presents to the ecology as a whole —
   * each plant host's effective traits, blended by the biome's own share of
   * that host (`hostShares`), with the un-hosted remainder reading exactly 1.
   * This is what the habitat's forage, the settlers' fire risk and the fire's
   * own fuel multiply: oak woodland takes its `spreading` strains at 0.60
   * weight, marsh takes almost nothing from anyone, and the blend at neutral
   * is exactly all-ones so every reader's untouched path multiplies by one.
   */
  plantTraits(biome: BiomeKey, wx: number, wz: number, now: number, out: StrainTraits): void {
    const gx = Math.floor(wx / STRAIN_CELL), gz = Math.floor(wz / STRAIN_CELL);
    const live = this.plantTiltAt(wx, wz, now);
    if (!live) {
      const key = `${biome}:${gx},${gz}`;
      const hit = this.plantBase.get(key);
      if (hit) {
        out.yield = hit[0]; out.tolerance = hit[1]; out.exposure = hit[2]; out.spread = hit[3];
        return;
      }
    }
    const { shares, rest } = hostShares(biome);
    let y = rest, t = rest, e = rest, s = rest;
    for (const h of shares) {
      if (live) {
        effectiveTraitsAt(this.region, this.field, h.host, wx, wz, now, this.scratch, this.mix, this.tilts);
        y += h.share * this.scratch.yield; t += h.share * this.scratch.tolerance;
        e += h.share * this.scratch.exposure; s += h.share * this.scratch.spread;
      } else {
        const b = this.baselineOf(h.host, gx, gz);
        y += h.share * b[0]; t += h.share * b[1]; e += h.share * b[2]; s += h.share * b[3];
      }
    }
    if (live) {
      out.yield = y; out.tolerance = t; out.exposure = e; out.spread = s;
      return;
    }
    // Stored FIRST and read back out of the store, so the cold call and every
    // warm call return the same float32-rounded numbers. The first cut wrote
    // the rounded copy and returned the float64 locals, and the divergence
    // was found the only way it could be: a restored world's FIRST habitat
    // read disagreed with its donor's warmed one at the eighth decimal, and
    // two days later the herds had drifted apart (test/persistence.ts). A
    // cache that answers more precisely on a miss than on a hit is not a
    // cache; it is two functions wearing one name.
    const stored = new Float32Array([y, t, e, s]);
    this.plantBase.set(`${biome}:${gx},${gz}`, stored);
    out.yield = stored[0]; out.tolerance = stored[1];
    out.exposure = stored[2]; out.spread = stored[3];
  }

  /**
   * The biome's dominant plant host at a point, for fire's selection press —
   * null where the biome grows none of the five. A constant per biome.
   */
  dominantHost(biome: BiomeKey): StrainHost | null {
    const { shares } = hostShares(biome);
    let best: StrainHost | null = null, bestShare = 0;
    for (const h of shares) if (h.share > bestShare) { best = h.host; bestShare = h.share; }
    return best;
  }
}
